import React, { useState, useEffect } from 'react';
import { Save, Loader2, Mail, Phone, MapPin, Globe, Github, Linkedin, Twitter, Instagram, Youtube } from 'lucide-react';
import { useApi } from '../AdminApp';

const ContactEditor = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tr');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [contactData, setContactData] = useState({
    email: '',
    phone: '',
    location: '',
    website: '',
    social: {
      github: '',
      linkedin: '',
      twitter: '',
      instagram: '',
      youtube: '',
      medium: ''
    },
    availability: { tr: '', en: '', de: '' }
  });

  useEffect(() => {
    fetchContact();
  }, []);

  const fetchContact = async () => {
    try {
      const data = await apiCall('/contact');
      setContactData(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await apiCall('/contact', {
        method: 'PUT',
        body: JSON.stringify(contactData)
      });
      setMessage({ type: 'success', text: 'Contact information updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value, nested = null) => {
    if (nested) {
      setContactData(prev => ({
        ...prev,
        [field]: { ...prev[field], [nested]: value }
      }));
    } else {
      setContactData(prev => ({ ...prev, [field]: value }));
    }
  };

  const tabs = [
    { id: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { id: 'en', label: 'English', flag: '🇺🇸' },
    { id: 'de', label: 'Deutsch', flag: '🇩🇪' }
  ];

  const socialLinks = [
    { key: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
    { key: 'twitter', label: 'Twitter', icon: Twitter, placeholder: 'https://twitter.com/username' },
    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
    { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel' },
    { key: 'medium', label: 'Medium', icon: Globe, placeholder: 'https://medium.com/@username' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Contact Information</h1>
          <p className="text-gray-400 mt-1">Manage your contact details and social links</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Basic Contact Info */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Mail className="w-4 h-4" /> Email Address
            </label>
            <input
              type="email"
              value={contactData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="contact@example.com"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Phone className="w-4 h-4" /> Phone Number
            </label>
            <input
              type="tel"
              value={contactData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="+90 555 123 4567"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <MapPin className="w-4 h-4" /> Location
            </label>
            <input
              type="text"
              value={contactData.location || ''}
              onChange={(e) => handleChange('location', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Istanbul, Turkey"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Globe className="w-4 h-4" /> Website
            </label>
            <input
              type="url"
              value={contactData.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="https://yourwebsite.com"
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
        <div className="flex border-b border-gray-700/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition ${
                activeTab === tab.id
                  ? 'text-white bg-gray-700/50 border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
              }`}
            >
              <span>{tab.flag}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Availability Status ({activeTab.toUpperCase()})
          </label>
          <input
            type="text"
            value={contactData.availability?.[activeTab] || ''}
            onChange={(e) => handleChange('availability', e.target.value, activeTab)}
            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder={activeTab === 'tr' ? 'Yeni projelere açığım' : activeTab === 'en' ? 'Available for new projects' : 'Verfügbar für neue Projekte'}
          />
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Social Media Links</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {socialLinks.map(social => (
            <div key={social.key}>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <social.icon className="w-4 h-4" /> {social.label}
              </label>
              <input
                type="url"
                value={contactData.social?.[social.key] || ''}
                onChange={(e) => handleChange('social', e.target.value, social.key)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder={social.placeholder}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactEditor;
